import { AxiosResponse } from 'axios'
import axios from '@/services/axios'
import type {
  CartCreatePayload,
  CartUpdatePayload,
  CartInterface,
  InvoiceCreatePayload,
  InvoiceTransactionInterface,
  AddItemToCartPayload,
  Recommendation,
} from '@/models/shop/CartV2.types'
import type { InvoiceInterface } from '@/models/shop/InvoiceV2.types'

const SHOP_BASE_URI = process.env.VUE_APP_SHOP_V2_BASE_URI
const ENTITY = 'carts'

export default abstract class CartsApi {
  public static async list(
    arenaId: string,
    params: object,
  ): Promise<
    AxiosResponse<{
      data: CartInterface[]
      metadata: { count: number; page: number; size: number; sort: string }
    }>
  > {
    const url = `${SHOP_BASE_URI}/arenas/${arenaId}/${ENTITY}`
    return await axios.get(url, { params })
  }

  public static async create(
    arenaId: string,
    data: CartCreatePayload,
  ): Promise<AxiosResponse<CartInterface>> {
    const url = `${SHOP_BASE_URI}/arenas/${arenaId}/${ENTITY}`
    return await axios.post(url, data)
  }

  public static async get(
    arenaId: string,
    id: string,
  ): Promise<AxiosResponse<CartInterface>> {
    const url = `${SHOP_BASE_URI}/arenas/${arenaId}/${ENTITY}/${id}`
    return await axios.get(url)
  }

  public static async update(
    arenaId: string,
    id: string,
    data: CartUpdatePayload,
  ): Promise<AxiosResponse<CartInterface>> {
    const url = `${SHOP_BASE_URI}/arenas/${arenaId}/${ENTITY}/${id}`
    return await axios.patch(url, data)
  }

  public static async createInvoice(
    arenaId: string,
    id: string,
    data: InvoiceCreatePayload,
  ): Promise<
    AxiosResponse<{
      invoice: InvoiceInterface
      transaction: InvoiceTransactionInterface
    }>
  > {
    const url = `${SHOP_BASE_URI}/arenas/${arenaId}/${ENTITY}/${id}/checkout`
    return await axios.post(url, data)
  }

  public static async addItemToCart(
    arenaId: string,
    id: string,
    data: AddItemToCartPayload,
  ): Promise<AxiosResponse<CartInterface>> {
    const url = `${SHOP_BASE_URI}/arenas/${arenaId}/${ENTITY}/${id}/items`
    return await axios.post(url, data)
  }

  public static async setItemQuantity(
    arenaId: string,
    id: string,
    itemId: string,
    data: { quantity: number },
  ): Promise<AxiosResponse<CartInterface>> {
    const url = `${SHOP_BASE_URI}/arenas/${arenaId}/${ENTITY}/${id}/items/${itemId}/set-quantity`
    return await axios.post(url, data)
  }

  public static async removeItemFromCart(
    arenaId: string,
    id: string,
    itemId: string,
  ): Promise<AxiosResponse<CartInterface>> {
    const url = `${SHOP_BASE_URI}/arenas/${arenaId}/${ENTITY}/${id}/items/${itemId}`
    return await axios.delete(url)
  }

  public static async removePromotionFromCart(
    arenaId: string,
    id: string,
    promotion_id: string,
    promotion_instance: number,
  ): Promise<AxiosResponse<CartInterface>> {
    const url = `${SHOP_BASE_URI}/arenas/${arenaId}/${ENTITY}/${id}/promotions/${promotion_id}/instance/${promotion_instance}`
    return await axios.delete(url)
  }

  public static async getRecommendations(
    arenaId: string,
    id: string,
  ): Promise<AxiosResponse<Recommendation[]>> {
    const url = `${SHOP_BASE_URI}/arenas/${arenaId}/${ENTITY}/${id}/promotions`
    return await axios.get(url)
  }
}
